import {
  getTransactionAmountDisplay,
  type TransactionDisplayInput,
} from '../../../utils/transactionDisplayUtils';

export type GgusonepayManualCheckResult = {
  transactionId: string;
  paymentTransactionId: number;
  type: 'deposit' | 'withdrawal';
  localStatus: string;
  gatewayState: number | null;
  gatewayStateLabel: string | null;
  gatewayOrderNo: string | null;
  currency: string;
  expectedAmountUsd: number | null;
  gatewayAmountUsd: number | null;
  gatewayRealAmountUsd: number | null;
  amountMismatch: boolean;
  amountMismatchWarning: string | null;
  isGatewaySuccess: boolean;
  canSettle: boolean;
  canAddCredit: boolean;
  canCompletePayout: boolean;
  canRefundPlayer?: boolean;
  playerRefunded?: boolean;
  alreadyRefunded?: boolean;
  payoutRefund?: {
    refunded?: boolean;
    alreadyRefunded?: boolean;
    refundAmount?: number | null;
    skipReason?: string | null;
  } | null;
  tierSettlementWarning?: string | null;
  settled?: boolean;
};

export type GgusReviewOutcome =
  | 'ready_to_resolve'
  | 'already_completed'
  | 'gateway_success_blocked'
  | 'gateway_pending'
  | 'gateway_failed'
  | 'order_not_found'
  | 'amount_mismatch'
  | 'not_checked'
  | 'check_error';

const GGUS_STATE_LABELS: Record<number, string> = {
  0: 'Order created',
  1: 'In progress',
  2: 'Success',
  3: 'Failed',
  4: 'Timed out',
  5: 'Refunded',
  6: 'Expired',
  7: 'Disputed',
};

function toFiniteUsd(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function resolvePanelAmountUsd(
  transaction: TransactionDisplayInput,
  checkResult?: GgusonepayManualCheckResult | null,
): number | null {
  const fromCheck =
    toFiniteUsd(checkResult?.expectedAmountUsd) ?? toFiniteUsd(checkResult?.gatewayAmountUsd);
  if (fromCheck != null) return fromCheck;

  const display = getTransactionAmountDisplay(transaction);
  if (display.mode === 'usd') {
    const usd = toFiniteUsd(display.amount);
    if (usd != null) return usd;
  }
  if (display.mode === 'split_cash_credits') {
    const cash = toFiniteUsd(display.cashPaid ?? display.cashReceived);
    if (cash != null) return cash;
  }

  const funding = transaction.fundingSummary;
  const fromFunding =
    toFiniteUsd(funding?.usdAmount) ??
    toFiniteUsd(funding?.cashCharged) ??
    toFiniteUsd(transaction.metadata?.cashCharged ?? transaction.metadata?.cash_charged);
  if (fromFunding != null) return fromFunding;

  return toFiniteUsd(transaction.amount);
}

export function formatUsd(amount: number | string | null | undefined): string {
  const n = toFiniteUsd(amount);
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
}

export function formatPaymentMethod(method: string | null | undefined): string {
  const raw = String(method || '').trim();
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatGgusGatewayLabel(
  state: number | null | undefined,
  stateLabel: string | null | undefined,
): string {
  if (state == null) return 'Unknown';
  if (GGUS_STATE_LABELS[state]) return GGUS_STATE_LABELS[state];
  if (stateLabel) return stateLabel.replace(/_/g, ' ');
  return String(state);
}

export function parseGgusApiError(message: string): { title: string; hint: string | null } {
  const normalized = message.trim();
  if (/order does not exist/i.test(normalized)) {
    return {
      title: 'Order not found on GGUS',
      hint:
        'GGUS has no record for this transaction. The payout/deposit may never have been submitted, or it was created under a different merchant account.',
    };
  }
  if (/amount mismatch/i.test(normalized)) {
    return {
      title: 'Amount mismatch',
      hint: 'Gateway amount does not match our records. Review amounts before settling.',
    };
  }
  if (/state=2|requires success/i.test(normalized)) {
    return {
      title: 'GGUS has not reported success yet',
      hint: 'Wait until GGUS shows Success, then check again.',
    };
  }
  return { title: normalized, hint: null };
}

export function deriveReviewOutcome(
  transactionStatus: string,
  checkResult: GgusonepayManualCheckResult | null,
  errorMessage: string | null,
): GgusReviewOutcome {
  if (errorMessage) {
    if (/order does not exist/i.test(errorMessage)) return 'order_not_found';
    return 'check_error';
  }
  if (!checkResult) return 'not_checked';

  if (transactionStatus === 'completed' || checkResult.localStatus === 'completed') {
    return 'already_completed';
  }
  if (checkResult.amountMismatch) return 'amount_mismatch';
  if (checkResult.canAddCredit || checkResult.canCompletePayout) return 'ready_to_resolve';
  if (checkResult.isGatewaySuccess && !checkResult.canSettle) return 'gateway_success_blocked';
  if (checkResult.gatewayState === 1 || checkResult.gatewayState === 0) return 'gateway_pending';
  if (checkResult.gatewayState === 3 || checkResult.gatewayState === 6) return 'gateway_failed';
  return 'gateway_pending';
}

export function getOutcomeCopy(
  outcome: GgusReviewOutcome,
  type: 'deposit' | 'withdrawal',
): { title: string; description: string; tone: 'success' | 'warning' | 'error' | 'info' } {
  switch (outcome) {
    case 'ready_to_resolve':
      return {
        title: type === 'deposit' ? 'Ready to add credit' : 'Ready to complete payout',
        description:
          type === 'deposit'
            ? 'GGUS reports success and amounts match. You can add credit and mark this deposit completed.'
            : 'GGUS reports success and amounts match. You can mark this withdrawal completed.',
        tone: 'success',
      };
    case 'already_completed':
      return {
        title: 'Already completed',
        description: 'This transaction is completed in our system. You can re-check GGUS for audit purposes.',
        tone: 'info',
      };
    case 'amount_mismatch':
      return {
        title: 'Amount mismatch — do not settle',
        description: 'Gateway amount differs from our expected amount. Escalate before manual settlement.',
        tone: 'error',
      };
    case 'order_not_found':
      return {
        title: 'Order not found on GGUS',
        description:
          'GGUS returned no order for this transaction. Confirm the order exists in the GGUS portal before retrying.',
        tone: 'error',
      };
    case 'gateway_failed':
      return {
        title: 'GGUS reports failed or expired',
        description:
          'The gateway did not succeed. Manual settlement is not available. Player credits are refunded automatically when eligible.',
        tone: 'warning',
      };
    case 'gateway_pending':
      return {
        title: 'GGUS still processing',
        description: 'Payment or payout is not successful on GGUS yet. Check again later.',
        tone: 'warning',
      };
    case 'check_error':
      return {
        title: 'Unable to check GGUS',
        description: 'The gateway query failed. See the error message below and try again.',
        tone: 'error',
      };
    default:
      return {
        title: 'Check GGUS status',
        description: 'Query the gateway to compare our records with GGUS before taking action.',
        tone: 'info',
      };
  }
}

export function getSettleActionLabel(type: 'deposit' | 'withdrawal'): string {
  return type === 'deposit' ? 'Add credit & complete' : 'Complete payout';
}
